import { createFileRoute, redirect, useNavigate, Outlet, useChildMatches } from '@tanstack/react-router'
import { AccountManagementPage } from '../pages/AccountManagementPage'
import { AccountType } from '../types/api'
import { useAuthStore } from '../stores/auth-store'

/**
 * Filters carried in the URL.
 *
 * They exist so the dashboard's "View all N accounts" lands on a list that
 * still reflects what the user was looking at, rather than dumping them into
 * an unfiltered table and making them re-narrow it. Living in the URL rather
 * than in navigation state also makes the filtered view shareable and
 * survivable across a reload.
 */
export interface AccountsSearch {
  type?: AccountType
  institution?: string
}

const ACCOUNT_TYPES: AccountType[] = ['Asset', 'Debt', 'Investment']

export const Route = createFileRoute('/accounts')({
  component: Accounts,
  // Validated rather than trusted: a hand-edited ?type=Nonsense must degrade
  // to "no filter", not render an empty table the user cannot explain.
  validateSearch: (search: Record<string, unknown>): AccountsSearch => {
    const type = search.type
    const institution = search.institution
    return {
      type:
        typeof type === 'string' && ACCOUNT_TYPES.includes(type as AccountType)
          ? (type as AccountType)
          : undefined,
      institution:
        typeof institution === 'string' && institution.trim() !== ''
          ? institution.trim()
          : undefined,
    }
  },
  beforeLoad: () => {
    const { isAuthenticated } = useAuthStore.getState()
    if (!isAuthenticated) {
      throw redirect({
        to: '/login',
      })
    }
  },
})

function Accounts() {
  const navigate = useNavigate()
  const { type, institution } = Route.useSearch()

  // `/accounts/$id` is registered as a CHILD of this route, so it renders into
  // this component's Outlet. Without one, navigating to an account showed the
  // full account LIST instead of the account -- the child matched, rendered
  // nowhere, and the parent carried on as if nothing had happened.
  //
  // Rendering the Outlet alone when a child is active keeps the two views
  // separate without restructuring the route tree.
  const childMatched = useChildMatches().length > 0
  if (childMatched) {
    return <Outlet />
  }

  const handleLogout = () => {
    navigate({ to: '/login' })
  }

  return (
    <AccountManagementPage
      onLogout={handleLogout}
      initialTypeFilter={type}
      initialInstitutionQuery={institution}
    />
  )
}
